import type { QueryClient } from "@tanstack/react-query";

/**
 * Centralized cache invalidation for ALL bill / expense / invoice mutations.
 *
 * Every CRUD, status-change, payment, void, or import operation against
 * `public.bills`, `public.project_expenses`, or `public.invoices` MUST funnel
 * through this helper so that:
 *   - Project Detail tabs (Financials, Expenses, Bills, Invoices)
 *   - Controller Dashboard KPI tiles
 *   - Vendor Detail outlays + 1099 summary
 *   - Global list pages (Bills, Invoices, Expenses)
 *   - Settings → QuickBooks integration sync log
 * all refresh automatically without a manual page reload.
 *
 * Convention: any TanStack query that reads from these domains MUST start
 * its `queryKey` with one of the well-known root strings listed below
 * (e.g. `["bills", projectId]`, `["invoices", "project", projectId]`,
 * `["project_expenses", projectId]`). Predicate matching handles the rest.
 *
 * NOTE: This codebase has no separate `invoice_payments` / `bill_payments`
 * tables — payment state lives on `amount_paid` / `status` / `paid_at` of
 * the parent record. Invalidating the parent list is enough.
 */

export interface FinancialsScope {
  projectId?: string | null;
  vendorId?: string | null;
  billId?: string | null;
  invoiceId?: string | null;
}

// Root keys whose every cached entry should be invalidated on any
// bill/expense/invoice write. Predicate-matched, so partial keys like
// `["bills", projectId, status]` are also caught.
const FINANCIAL_ROOT_KEYS = [
  // Lists & per-project rollups
  "bills",
  "wo_bills",
  "bill_sections",
  "bill_line_items",
  "bill_payment_log",
  "bill_attachments",
  "invoices",
  "invoice-detail",
  "invoice-lines",
  "project_expenses",
  "all_expenses",
  // KPI / financial rollups
  "kpi_invoices",
  "kpi_bills",
  "kpi_contracts",
  "kpi_committed_cost",
  "kpi_sub_variance",
  "kpi_sov_co_lines",
  "kpi_sov_budget",
  "project_billed_to_date",
  "project_collected_to_date",
  "project_contract_value",
  "project_estimated_cost",
  "project_committed_cost",
  "project_actual_cost",
  "project_variance_drivers",
  // Cross-cutting consumers
  "dashboard_metrics",
  "approval_counts",
  "approval_queue",
  "work_order_lines",
  "sov_lines",
  // Vendor / 1099 surfaces
  "vendor_expenses",
  "vendor_outlays",
  "vendor_bills",
  "vendor_payment_history",
  "1099_summary",
  // Personnel
  "personnel_invoices",
  "personnel_vendor_bills",
  // QuickBooks integration views
  "qb_sync_log",
  "qb_bills",
  "qb_estimates",
  "chart_of_accounts",
] as const;

const ROOT_SET = new Set<string>(FINANCIAL_ROOT_KEYS);

/**
 * Invalidate every query touched by a bill / expense / invoice mutation.
 *
 * @param qc    The TanStack QueryClient
 * @param scope Optional record-specific keys to also invalidate. All fields
 *              are optional — omit to broadly invalidate (e.g. for QB import).
 */
export function invalidateFinancials(
  qc: QueryClient,
  scope: FinancialsScope = {},
) {
  // Broad predicate sweep — covers list, detail, KPI, vendor, QB views.
  qc.invalidateQueries({
    predicate: (q) => {
      const root = q.queryKey?.[0];
      return typeof root === "string" && ROOT_SET.has(root);
    },
  });

  // Targeted record-level keys (cheap, idempotent).
  if (scope.billId) {
    qc.invalidateQueries({ queryKey: ["bill", scope.billId] });
    qc.invalidateQueries({ queryKey: ["bill_attachments", scope.billId] });
    qc.invalidateQueries({ queryKey: ["bill_line_items", scope.billId] });
  }
  if (scope.invoiceId) {
    qc.invalidateQueries({ queryKey: ["invoice-detail", scope.invoiceId] });
    qc.invalidateQueries({ queryKey: ["invoice-lines", scope.invoiceId] });
  }
  // projectId / vendorId are already covered by the predicate sweep
  // (their queries start with one of the FINANCIAL_ROOT_KEYS), but keep
  // the params in the public signature for call-site clarity & future use.
  void scope.projectId;
  void scope.vendorId;
}

/**
 * @deprecated Use `invalidateFinancials(qc, { projectId })` instead.
 * Retained so existing imports keep compiling without churn.
 */
export function invalidateProjectFinancials(
  qc: QueryClient,
  projectId?: string | null,
) {
  invalidateFinancials(qc, { projectId });
}
